import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { FaExclamationCircle } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { getRemovedWorkerTimesheetWarningCopy } from "./advanceModeUi";

/**
 * Attractive confirmation dialog when updating a shift after removing
 * originally assigned workers (timesheets will also be removed).
 */
const AdvanceModeRemoveWorkerConfirmModal = ({
  isOpen = false,
  workers = [],
  onConfirm,
  onCancel,
}) => {
  const copy = getRemovedWorkerTimesheetWarningCopy(workers);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onCancel]);

  if (!isOpen || !copy || typeof document === "undefined") return null;

  const names = workers
    .map((w) => w?.name || w?.label)
    .filter(Boolean);

  return createPortal(
    <div
      className='fixed inset-0 z-[100050] flex items-center justify-center p-4 sm:p-6'
      role='dialog'
      aria-modal='true'
      aria-labelledby='advance-remove-worker-confirm-title'
    >
      <button
        type='button'
        className='absolute inset-0 bg-slate-900/50 backdrop-blur-[2px] transition-opacity'
        aria-label='Close'
        onClick={onCancel}
      />

      <div className='advance-remove-worker-confirm relative w-full max-w-md overflow-hidden rounded-2xl border border-rose-200/80 bg-white shadow-2xl shadow-rose-900/15'>
        <div className='h-1.5 w-full bg-gradient-to-r from-rose-400 via-rose-500 to-red-500' />

        <button
          type='button'
          onClick={onCancel}
          className='absolute right-3 top-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
          aria-label='Cancel'
        >
          <HiX className='h-5 w-5' />
        </button>

        <div className='px-5 pb-5 pt-6 sm:px-6 sm:pb-6'>
          <div className='mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-rose-100 to-red-50 text-rose-600 ring-8 ring-rose-50'>
            <FaExclamationCircle className='h-7 w-7' aria-hidden />
          </div>

          <h2
            id='advance-remove-worker-confirm-title'
            className='mt-4 text-center text-lg font-semibold tracking-tight text-slate-900'
          >
            {copy.title}
          </h2>

          {names.length > 0 && (
            <div className='mt-4 flex flex-wrap justify-center gap-1.5'>
              {names.map((name) => (
                <span
                  key={name}
                  className='inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold capitalize text-rose-800'
                >
                  {name}
                </span>
              ))}
            </div>
          )}

          <p className='mt-4 text-center text-sm leading-relaxed text-slate-600'>
            {copy.content}
          </p>

          <div className='mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-center'>
            <button
              type='button'
              onClick={onCancel}
              className='inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={onConfirm}
              className='inline-flex h-10 items-center justify-center rounded-xl bg-gradient-to-r from-rose-600 to-red-600 px-5 text-sm font-semibold text-white shadow-md shadow-rose-600/25 transition hover:from-rose-700 hover:to-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2'
            >
              Proceed & update shift
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AdvanceModeRemoveWorkerConfirmModal;
